
import { useState, useEffect, useMemo } from 'react';
import { Search, X, Mic } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';

interface SearchHeaderProps {
  onSearch: (query: string) => void;
}

const SearchHeader = ({ onSearch }: SearchHeaderProps) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isListening, setIsListening] = useState(false);
  const { toast } = useToast();

  // Debounced search - automatically search after user stops typing
  const debouncedSearchQuery = useMemo(() => {
    const timeoutId = setTimeout(() => {
      if (searchQuery.trim()) {
        onSearch(searchQuery);
      }
    }, 300);
    
    return () => clearTimeout(timeoutId);
  }, [searchQuery, onSearch]);

  useEffect(() => {
    return debouncedSearchQuery;
  }, [debouncedSearchQuery]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(searchQuery);
    toast({
      title: "Search completed",
      description: `Found results for "${searchQuery}"`,
    });
  };

  const handleClear = () => {
    setSearchQuery('');
    onSearch('');
    toast({
      title: "Search cleared",
      description: "Showing all stations",
    });
  };

  const handleVoiceSearch = async () => {
    if (!('webkitSpeechRecognition' in window)) {
      toast({
        title: "Voice search not supported",
        description: "Please use text search instead",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsListening(true);
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'en-US';

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setSearchQuery(transcript);
        onSearch(transcript);
        toast({
          title: "Voice search completed",
          description: `Searching for "${transcript}"`,
        });
      };

      recognition.onerror = () => {
        toast({
          title: "Voice search failed",
          description: "Please try again or use text search",
          variant: "destructive",
        });
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } catch (error) {
      setIsListening(false);
      toast({
        title: "Voice search error",
        description: "Please try text search instead",
        variant: "destructive",
      });
    }
  };

  return (
    <div className="w-full bg-background/95 backdrop-blur-sm py-4 sticky top-0 z-10 border-b border-border/40">
      <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder="Search by location, charger type, or amenities..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-20 transition-all duration-200 focus:ring-2 focus:ring-primary/20"
          />
          {searchQuery && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={handleClear}
              className="absolute right-12 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-destructive/10"
            >
              <X className="h-3 w-3" />
            </Button>
          )}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleVoiceSearch}
            disabled={isListening}
            className="absolute right-2 top-1/2 transform -translate-y-1/2 h-8 w-8 p-0 hover:bg-primary/10"
          >
            <Mic className={`h-3 w-3 ${isListening ? 'text-red-500 animate-pulse' : 'text-muted-foreground'}`} />
          </Button>
        </div>
        <div className="flex gap-2">
          <Button 
            type="submit" 
            variant="default"
            className="transition-all duration-200 hover:shadow-md min-w-[100px]"
          >
            Search
          </Button>
        </div>
      </form>
    </div>
  );
};

export default SearchHeader;
